import React, { useState, useMemo, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { commitActivityAPI } from '../../services/api';
import { commitActivityKeys } from '../../query/queryKeys';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];
const HEATMAP_COLORS = [
  'var(--heatmap-0)',
  'var(--heatmap-1)',
  'var(--heatmap-2)',
  'var(--heatmap-3)',
  'var(--heatmap-4)',
  'var(--heatmap-5)',
];

const METRIC_OPTIONS = [
  { value: 'commits', label: 'Commits' },
  { value: 'pull_requests', label: 'Pull requests' },
  { value: 'additions', label: 'Additions' },
  { value: 'deletions', label: 'Deletions' },
];

type HeatmapCell = {
  dateStr: string | null;
  count: number;
  isFuture: boolean;
  empty: boolean;
};

type MonthBlock = {
  month: number;
  label: string;
  numCols: number;
  grid: HeatmapCell[][];
};

function getLevel(count, maxCount) {
  if (count <= 0) return 0;
  if (maxCount <= 0) return 0;
  const ratio = count / maxCount;
  if (ratio <= 0.25) return 1;
  if (ratio <= 0.5) return 2;
  if (ratio <= 0.75) return 3;
  if (ratio < 1) return 4;
  return 5;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

const CELL_SIZE = 11;
const CELL_GAP = 3;
const COLUMN_WIDTH = CELL_SIZE + CELL_GAP;
const MONTH_BLOCK_GAP = 5;
const MIN_COLUMNS_PER_BLOCK = 6;
const BLOCK_INSET = 6;

/**
 * Build 12 month blocks; each month is a 7 (rows) x numCols (columns) grid.
 * Uses UTC for dates so dateStr matches backend (API returns UTC YYYY-MM-DD).
 * Rows are Monday (0) … Sunday (6). numCols = ceil((firstRow + daysInMonth) / 7).
 * Returns { monthBlocks: { month, label, numCols, grid[7][numCols] }[], maxCount }.
 */
function buildGrid(data: any[], year: number) {
  const countByDate = new Map((data || []).map((d: any) => [d.date, Number(d.count) || 0]));
  const now = new Date();
  const todayUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  let maxCount = 0;

  const monthBlocks: MonthBlock[] = [];
  for (let m = 0; m < 12; m++) {
    const daysInMonth = getDaysInMonth(year, m);
    const firstDayUTC = new Date(Date.UTC(year, m, 1, 0, 0, 0, 0));
    const firstWeekdayUTC = firstDayUTC.getUTCDay();
    const firstRow = (firstWeekdayUTC + 6) % 7;
    const numCols = Math.ceil((firstRow + daysInMonth) / 7);

    const grid: HeatmapCell[][] = Array(7)
      .fill(null)
      .map(() =>
        Array(numCols)
          .fill(null)
          .map((): HeatmapCell => ({ dateStr: null, count: 0, isFuture: false, empty: true }))
      );

    for (let col = 0; col < numCols; col++) {
      for (let row = 0; row < 7; row++) {
        const cellIndex = col * 7 + row;
        const dayOfMonth = cellIndex - firstRow + 1;
        if (dayOfMonth < 1 || dayOfMonth > daysInMonth) {
          grid[row][col] = { dateStr: null, count: 0, isFuture: false, empty: true };
          continue;
        }
        const d = new Date(Date.UTC(year, m, dayOfMonth, 0, 0, 0, 0));
        const dateStr = d.toISOString().slice(0, 10);
        const count = countByDate.get(dateStr) || 0;
        const cellUTC = d.getTime();
        const isFuture = cellUTC > todayUTC;
        grid[row][col] = { dateStr, count, isFuture, empty: false };
        if (count > maxCount) maxCount = count;
      }
    }

    monthBlocks.push({
      month: m,
      label: MONTH_LABELS[m],
      numCols,
      grid,
    });
  }

  // Explicit Jan (0) → Dec (11) order; return new array so display order is guaranteed
  const sortedBlocks = [...monthBlocks].sort((a, b) => a.month - b.month);
  const maxNumCols = Math.max(0, ...sortedBlocks.map((mb) => mb.numCols));

  return { monthBlocks: sortedBlocks, maxCount, maxNumCols };
}

function formatTooltipDate(dateStr) {
  const d = new Date(dateStr + 'T12:00:00Z');
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getMetricLabel(metric, count) {
  if (metric === 'commits') return count === 1 ? '1 commit' : `${count} commits`;
  if (metric === 'pull_requests') return count === 1 ? '1 pull request' : `${count} pull requests`;
  if (metric === 'additions') return count === 1 ? '1 addition' : `${count} additions`;
  if (metric === 'deletions') return count === 1 ? '1 deletion' : `${count} deletions`;
  return `${count}`;
}

/**
 * GitHub-style activity heatmap (commits, PRs, additions, deletions).
 * Props: scope, organisationId, streamId?, projectId?, repositoryId?, memberId?, memberIds?, year?, metric?, title?, fetchEnabled?
 */
const CommitHeatmap = ({
  scope = 'organisation',
  organisationId,
  streamId,
  projectId,
  repositoryId,
  memberId,
  memberIds,
  year: yearProp,
  metric: metricProp,
  title = 'Activity Heatmap',
  /** When false, skips fetching (e.g. parent tab not visible) to avoid idle network work. */
  fetchEnabled = true,
}: any) => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(yearProp != null ? yearProp : currentYear);
  const [selectedMetric, setSelectedMetric] = useState(metricProp || 'commits');
  const [tooltip, setTooltip] = useState<any>(null);
  const containerRef = useRef<any>(null);

  const year = yearProp != null ? yearProp : selectedYear;
  const metric = metricProp || selectedMetric;

  const params = useMemo(() => {
    const p: any = { scope, organisationId, year, metric };
    if (streamId) p.streamId = streamId;
    if (projectId) p.projectId = projectId;
    if (repositoryId) p.repositoryId = repositoryId;
    if (memberId) p.memberId = memberId;
    if (memberIds?.length) p.memberIds = Array.isArray(memberIds) ? memberIds.join(',') : memberIds;
    return p;
  }, [scope, organisationId, streamId, projectId, repositoryId, memberId, memberIds, year, metric]);

  const canFetch =
    fetchEnabled &&
    ((scope !== 'repository' && !!organisationId) || (scope === 'repository' && !!repositoryId));

  const activityQuery = useQuery({
    queryKey: commitActivityKeys.activity(params),
    queryFn: async () => {
      const res = await commitActivityAPI.getActivity(params);
      const raw = res?.data?.data ?? res?.data;
      return Array.isArray(raw) ? raw : [];
    },
    enabled: canFetch,
    staleTime: 60_000,
  });

  const data = activityQuery.data;
  const loading = canFetch && activityQuery.isPending;
  const error = activityQuery.error
    ? activityQuery.error.message || 'Failed to load activity'
    : null;

  const { monthBlocks, maxCount, maxNumCols } = useMemo(
    () => buildGrid(data || [], year),
    [data, year]
  );

  const columnsForWidth = Math.max(maxNumCols, MIN_COLUMNS_PER_BLOCK);
  const blockWidth = columnsForWidth * COLUMN_WIDTH - CELL_GAP + BLOCK_INSET;

  const years = useMemo(() => {
    const list: number[] = [];
    for (let y = currentYear; y >= currentYear - 4; y--) list.push(y);
    return list;
  }, [currentYear]);

  const handleCellMouseEnter = useCallback((e, cell) => {
    if (cell.empty || !cell.dateStr) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) {
      setTooltip({ ...cell, x: e.clientX, y: e.clientY });
      return;
    }
    setTooltip({
      ...cell,
      x: e.clientX,
      y: e.clientY,
    });
  }, []);

  const handleCellMouseLeave = useCallback(() => {
    setTooltip(null);
  }, []);

  if (loading) {
    return (
      <div className="card-mdl-static p-4">
        <h2 className="section-title-mdl mb-4">{title}</h2>
        <div className="h-32 rounded bg-dark-elevated animate-pulse" aria-busy="true" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-mdl-static p-4">
        <h2 className="section-title-mdl mb-4">{title}</h2>
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="card-mdl-static p-4 flex flex-col items-center" ref={containerRef}>
      <div className="flex flex-nowrap items-center justify-between gap-4 mb-4 w-full h-10">
        <h2 className="section-title-mdl shrink-0 mb-0 mt-0 flex items-center">{title}</h2>
        <div className="flex items-center gap-3 shrink-0">
          <select
            value={year}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="text-sm rounded border border-dark-border bg-dark-elevated text-slate-100 px-2 py-1 focus:ring-2 focus:ring-brand-purple/30 focus:border-brand-purple/50"
            aria-label="Select year"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
          <select
            value={metric}
            onChange={(e) => setSelectedMetric(e.target.value)}
            className="text-sm rounded border border-dark-border bg-dark-elevated text-slate-100 px-2 py-1 focus:ring-2 focus:ring-brand-purple/30 focus:border-brand-purple/50"
            aria-label="Select metric"
          >
            {METRIC_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto w-full flex justify-center" style={{ direction: 'ltr' }}>
        <div className="inline-flex flex-col items-center">
          {/* Month headers row – centered above each same-width block */}
          <div
            className="inline-flex items-center min-h-[1.25rem] text-xs text-slate-500 mb-1"
            style={{ gap: MONTH_BLOCK_GAP }}
          >
            <div className="flex-shrink-0 w-10 pr-1" aria-hidden />
            {monthBlocks.map((mb) => (
              <div
                key={mb.month}
                className="flex-shrink-0 text-center"
                style={{ width: blockWidth }}
                title={MONTH_LABELS[mb.month]}
              >
                {mb.label}
              </div>
            ))}
          </div>
          <div
            className="inline-flex items-stretch"
            style={{ direction: 'ltr', gap: MONTH_BLOCK_GAP }}
          >
            {/* Row-based layout: each of 7 rows has the day label + that row's cells so they stay aligned */}
            <div
              className="flex flex-col gap-[3px] pr-1 flex-shrink-0 w-10"
              style={{ paddingTop: 2, paddingBottom: 2 }}
            >
              {DAY_LABELS.map((label) => (
                <span
                  key={label}
                  className="text-xs text-slate-500 flex items-center flex-shrink-0"
                  style={{ height: CELL_SIZE }}
                >
                  {label}
                </span>
              ))}
            </div>
            {/* 12 month blocks: same width and border so Jan is left, Dec is right */}
            <div className="flex flex-shrink-0" style={{ direction: 'ltr', gap: MONTH_BLOCK_GAP }}>
              {monthBlocks.map((mb) => (
                <div
                  key={mb.month}
                  className="flex flex-col gap-[3px] rounded border border-dark-border p-0.5 box-border"
                  style={{ width: blockWidth, minWidth: blockWidth }}
                >
                  {Array.from({ length: 7 }, (_, row) => (
                    <div key={row} className="flex gap-[3px]">
                      {Array.from({ length: mb.numCols }, (_, col) => {
                        const cell = mb.grid[row]?.[col];
                        if (!cell) return null;
                        if (cell.empty) {
                          return (
                            <div
                              key={`${col}-${row}`}
                              className="w-3 h-3 rounded-sm flex-shrink-0 opacity-0"
                              style={{ width: CELL_SIZE, height: CELL_SIZE }}
                              aria-hidden
                            />
                          );
                        }
                        const { dateStr, count, isFuture } = cell;
                        const level = getLevel(count, maxCount);
                        return (
                          <div
                            key={`${col}-${row}`}
                            className={
                              isFuture
                                ? 'w-3 h-3 rounded-sm flex-shrink-0 cursor-default border border-dark-border-glow'
                                : 'w-3 h-3 rounded-sm flex-shrink-0 cursor-default border border-transparent'
                            }
                            style={{
                              width: CELL_SIZE,
                              height: CELL_SIZE,
                              backgroundColor: isFuture ? 'transparent' : HEATMAP_COLORS[level],
                            }}
                            role="img"
                            aria-label={
                              dateStr ? `${getMetricLabel(metric, count)} on ${dateStr}` : ''
                            }
                            onMouseEnter={(e) => handleCellMouseEnter(e, cell)}
                            onMouseLeave={handleCellMouseLeave}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-slate-500 w-full justify-center">
        <span>Less</span>
        <div className="flex gap-0.5">
          {HEATMAP_COLORS.map((color, i) => (
            <div
              key={i}
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: color }}
              aria-hidden
            />
          ))}
        </div>
        <span>More</span>
      </div>
      {tooltip != null && tooltip.dateStr && (
        <div
          className="fixed z-50 px-2 py-1.5 text-xs rounded shadow-lg bg-dark-elevated border border-dark-border text-slate-100 pointer-events-none"
          style={{
            left: Math.min(tooltip.x + 10, window.innerWidth - 140),
            top: tooltip.y - 36,
          }}
          role="tooltip"
        >
          <div className="font-medium">{getMetricLabel(metric, tooltip.count)}</div>
          <div className="text-slate-400">{formatTooltipDate(tooltip.dateStr)}</div>
        </div>
      )}
    </div>
  );
};

export default CommitHeatmap;
